import { useState, useEffect } from "react";
import axios from "axios";
import { Copy, Users, TrendingUp, Check } from "lucide-react";
import { toast } from "sonner";

import { API } from "@/apiConfig";

export default function MyTeamPage({ profile }) {
  const [teamInfo, setTeamInfo] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchTeamInfo();
  }, []);

  const fetchTeamInfo = async () => {
    try {
      const { data } = await axios.get(`${API}/team/info`);
      setTeamInfo(data);
    } catch {
      // ignore
    }
  };

  const inviteLink = `${window.location.origin}?ref=${profile?.invite_code || ""}`;

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      toast.error("Failed to copy");
    });
  };

  return (
    <div className="team-page" data-testid="myteam-page">
      <h1 className="page-title heading-serif">My Team</h1>

      {/* Invite Card */}
      <div className="invite-card" data-testid="invite-card">
        <h3 className="text-white font-medium mb-3">Invite Friends & Earn</h3>
        <p className="text-sm text-slate-400 mb-4">
          Share your invite code and earn <span className="text-[#d4af37] font-bold">20% commission</span> on your friend's first deposit!
        </p>

        <div className="mb-3">
          <label className="auth-label">Your Invite Code</label>
          <div className="invite-code-box" data-testid="invite-code-display">
            <span className="invite-code-text">{profile?.invite_code || "------"}</span>
            <button
              onClick={() => copyToClipboard(profile?.invite_code || "")}
              className="copy-btn"
              data-testid="copy-code-btn"
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
            </button>
          </div>
        </div>

        <div className="mb-4">
          <label className="auth-label">Invite Link</label>
          <div className="invite-code-box" data-testid="invite-link-display">
            <span className="invite-link-text">{inviteLink}</span>
            <button
              onClick={() => copyToClipboard(inviteLink)}
              className="copy-btn"
              data-testid="copy-link-btn"
            >
              <Copy size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="team-stats" data-testid="team-stats">
        <div className="team-stat-item">
          <Users size={20} className="text-[#d4af37]" />
          <div>
            <p className="stat-value">{teamInfo?.total_members || 0}</p>
            <p className="stat-label">Team Members</p>
          </div>
        </div>
        <div className="team-stat-item">
          <TrendingUp size={20} className="text-[#d4af37]" />
          <div>
            <p className="stat-value">&#8377;{teamInfo?.total_commission?.toFixed(2) || "0.00"}</p>
            <p className="stat-label">Total Commission</p>
          </div>
        </div>
      </div>

      {/* Members List */}
      <div className="team-members" data-testid="team-members">
        <h3 className="text-sm font-medium text-slate-400 mb-3">Invited Members</h3>
        {!teamInfo?.members?.length ? (
          <div className="empty-state">
            <Users size={32} className="text-slate-600 mx-auto mb-2" />
            <p className="text-slate-500 text-sm">No team members yet</p>
            <p className="text-slate-600 text-xs">Share your invite code to grow your team!</p>
          </div>
        ) : (
          teamInfo.members.map((member, i) => (
            <div key={i} className="member-item" data-testid={`member-${i}`}>
              <div className="member-avatar">
                <span>{member.masked_phone?.slice(0, 2) || "?"}</span>
              </div>
              <div>
                <p className="text-sm text-white">ID: {member.user_id}</p>
                <p className="text-xs text-slate-400">{member.masked_phone}</p>
              </div>
              <span className="text-xs text-slate-500 ml-auto">
                {new Date(member.joined_at).toLocaleDateString()}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
